import { brandAssets } from '@echo/brand';
import mascotNotifications from '@echo/brand/assets/mascot-notifications.png';
import { Button, ListRow, Switch } from '@echo/ui-web';
import { useLingui } from '@lingui/react';
import { Trans, useLingui as useLinguiMacro } from '@lingui/react/macro';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { EmptyStateArt, EmptyStateFrame, ToolbarBack } from '@/components/empty-state-frame';
import { REMINDER_CHANGED_EVENT } from '@/components/reminder-sync';
import { ReminderTimeSheet } from '@/components/reminder-time-sheet';
import { formatReminderTime } from '@/lib/reminder-copy';
import {
  DEFAULT_REMINDER,
  type ReminderPreference,
  readStoredReminder,
  storeReminder,
} from '@/lib/reminder-storage';
import {
  getReminderPermission,
  type ReminderPermission,
  requestReminderPermission,
} from '@/lib/reminders';

export const Route = createFileRoute('/_app/account_/reminders')({ component: RemindersPage });

/**
 * Account › Preferences › Reminders: the switch and the time are edited on the card and applied together by "Save
 * changes". The browser shows the notification while a tab with the app is open
 * (`lib/reminders.ts`); when notifications are blocked the page becomes the Figma's
 * "Notifications are off" state.
 */
function RemindersPage() {
  const { i18n } = useLingui();
  const { t } = useLinguiMacro();
  const navigate = useNavigate();
  const [permission, setPermission] = useState<ReminderPermission>(getReminderPermission);
  const [timeOpen, setTimeOpen] = useState(false);

  // Edits live here until "Save changes" stores them.
  const [draft, setDraft] = useState<ReminderPreference>(DEFAULT_REMINDER);
  useEffect(() => {
    setDraft(readStoredReminder());
  }, []);
  const back = () => void navigate({ to: '/account' });
  const save = () => {
    storeReminder(draft);
    window.dispatchEvent(new Event(REMINDER_CHANGED_EVENT));
    back();
  };

  const toggle = async (enabled: boolean) => {
    if (!enabled) {
      setDraft({ ...draft, enabled: false });
      return;
    }
    const granted = await requestReminderPermission().catch((): ReminderPermission => 'denied');
    setPermission(granted);
    // Keep the wish even when denied: once the browser allows it, the reminder is already on.
    setDraft({ ...draft, enabled: true });
  };

  const timeLabel = formatReminderTime(i18n, draft);

  if (draft.enabled && permission === 'denied') {
    return (
      <>
        <ToolbarBack
          render={({ className, children }) => (
            <Link to="/account" className={className}>
              {children}
            </Link>
          )}
        />
        <EmptyStateFrame
          art={
            <EmptyStateArt
              src={mascotNotifications}
              alt=""
              width={brandAssets.mascotNotifications.width}
              height={brandAssets.mascotNotifications.height}
            />
          }
          title={<Trans>Notifications are off</Trans>}
          actions={
            <>
              <Button variant="ghost" onClick={back}>
                {t`Not now`}
              </Button>
              {/* A browser has no settings screen the app can open: asking again is the only way in. */}
              <Button onClick={() => void toggle(true)}>{t`Allow notifications`}</Button>
            </>
          }
        >
          <Trans>
            StrongerMemory needs permission to send notifications to you. Turn this on in your
            browser's site settings.
          </Trans>
        </EmptyStateFrame>
      </>
    );
  }

  return (
    <>
      <ToolbarBack
        render={({ className, children }) => (
          <Link to="/account" className={className}>
            {children}
          </Link>
        )}
      />
      <h1 className="text-display-sm font-bold">
        <Trans>Reminders</Trans>
      </h1>
      <div className="flex flex-col gap-internal-md rounded-xl bg-surface p-content-sm">
        <p className="text-body-md text-on-surface-medium">
          <Trans>Get reminders to do your reading, math and writing exercises.</Trans>
        </p>
        <div className="flex flex-col">
          <ListRow
            label={t`Reminders`}
            value={draft.enabled ? t`On` : t`Off`}
            className={draft.enabled ? 'pl-0' : 'border-b-0 pl-0'}
            trailing={
              <Switch
                checked={draft.enabled}
                onCheckedChange={(next) => void toggle(next)}
                label={t`Reminders`}
              />
            }
          />
          {/* Only while reminders are on: a time row that ignored clicks read as broken. */}
          {draft.enabled ? (
            <ListRow
              label={t`Notify me at`}
              value={timeLabel}
              accent
              className="border-b-0 pl-0"
              trailing={<ChevronRight size={24} className="text-icon-medium" aria-hidden />}
              onClick={() => setTimeOpen(true)}
            />
          ) : null}
        </div>
        {permission === 'unsupported' ? (
          <p className="text-body-sm text-on-surface-medium">
            <Trans>
              This browser can't show notifications, so the reminder is kept for the app.
            </Trans>
          </p>
        ) : null}
      </div>
      <div className="mt-auto flex justify-end pt-content-lg">
        <Button onClick={save}>{t`Save changes`}</Button>
      </div>
      <ReminderTimeSheet
        open={timeOpen}
        selected={draft}
        onClose={() => setTimeOpen(false)}
        onDone={(time) => {
          setTimeOpen(false);
          setDraft({ ...draft, hour: time.hour, minute: time.minute });
        }}
      />
    </>
  );
}
